import React, { useState, useEffect, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import Select from "react-select";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Button from "react-bootstrap/Button";
import { useTicketPriorities } from "../../../hooks/TICKET/useTicketPriorities";
import { useTicketTypes } from "../../../hooks/TICKET/useTicketTypes";
import { useUsers } from "../../../hooks/useGetUsers";
import { useGroupes } from "../../../hooks/useGetGroupes";
import { useUpdateTicket } from "../../../hooks/TICKET/useUpdateTicket";
import MarkdownEditor from "../../MarkdownEditor/MarkdownEditor";
import { toast } from "react-toastify";
import { customSelectStyles } from "../../../utils/reactSelectStyles";
import {
  assignTicketUsers,
  removeTicketUsers,
  assignTicketGroupes,
  removeTicketGroupes,
} from "../../../api/TICKET/ticket";

const _pad = (n) => String(n).padStart(2, "0");
const _localDate = (d) => `${d.getFullYear()}-${_pad(d.getMonth() + 1)}-${_pad(d.getDate())}`;
const _localTime = (d) => `${_pad(d.getHours())}:${_pad(d.getMinutes())}`;

const EditTicket = ({ showEditTicket, setShowEditTicket, ticket }) => {
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dateDebut, setDateDebut] = useState("");
  const [heureDebut, setHeureDebut] = useState("");
  const [dateFin, setDateFin] = useState("");
  const [heureFin, setHeureFin] = useState("");
  const [checkdateFin, setCheckdateFin] = useState(false);

  const [titre, setTitre] = useState("");
  const [selectedType, setSelectedType] = useState(null);
  const [selectedPriorite, setSelectedPriorite] = useState(null);
  const [description, setDescription] = useState("");

  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);

  const { data: prioritiesData } = useTicketPriorities();
  const { data: typesData } = useTicketTypes();
  const { data: usersData } = useUsers();
  const { data: groupesData } = useGroupes();
  const updateTicketMutation = useUpdateTicket();

  const optionsType = useMemo(
    () => typesData?.data?.map((t) => ({ value: t.id, label: t.label })) || [],
    [typesData]
  );

  const optionsPriorite = useMemo(
    () => prioritiesData?.data?.map((p) => ({ value: p.id, label: p.label })) || [],
    [prioritiesData]
  );

  const optionsUsers = useMemo(
    () => (usersData?.data ?? []).map((u) => ({
      value: u.id,
      label: `${u.name || ""} ${u.surname || ""}`.trim(),
    })),
    [usersData]
  );

  const optionsGroups = useMemo(
    () => (groupesData?.data ?? []).map((g) => ({
      value: g.id,
      label: g.name,
    })),
    [groupesData]
  );

  useEffect(() => {
    if (showEditTicket && ticket) {
      setTitre(ticket.title || "");
      setDescription(ticket.description || "");

      if (ticket.start_date) {
        const d = new Date(ticket.start_date);
        setDateDebut(_localDate(d));
        setHeureDebut(_localTime(d));
      }

      if (ticket.end_date) {
        const d = new Date(ticket.end_date);
        setDateFin(_localDate(d));
        setHeureFin(_localTime(d));
        setCheckdateFin(true);
      } else {
        setCheckdateFin(false);
      }

      if (ticket.priority && optionsPriorite.length > 0) {
        const p = optionsPriorite.find((x) => x.value === ticket.priority.id);
        setSelectedPriorite(p || null);
      }

      if (ticket.type && optionsType.length > 0) {
        const t = optionsType.find((x) => x.value === ticket.type.id);
        setSelectedType(t || null);
      }

      const preUsers = (ticket.users ?? [])
        .map((u) => optionsUsers.find((x) => x.value === u.id))
        .filter(Boolean);
      setSelectedUsers(preUsers);

      const preGroups = (ticket.groups ?? [])
        .map((g) => optionsGroups.find((x) => x.value === g.id))
        .filter(Boolean);
      setSelectedGroups(preGroups);
    }
  }, [showEditTicket, ticket, optionsPriorite, optionsType, optionsUsers, optionsGroups]);

  const handleSubmitForm = async (e) => {
    e.preventDefault();

    if (!titre.trim()) return toast.error("Titre requis");
    if (!selectedType) return toast.error("Type requis");
    if (!selectedPriorite) return toast.error("Priorité requise");

    const payload = {
      titre,
      description: description || null,
      date_debut: `${dateDebut}T${heureDebut}:00`,
      ...(checkdateFin && { date_fin: `${dateFin}T${heureFin}:00` }),
      priorite: selectedPriorite.value,
      type: selectedType.value,
    };

    setIsSubmitting(true);
    try {
      await updateTicketMutation.mutateAsync({ id: ticket.id, ticketData: payload });

      const oldUserIds = (ticket.users ?? []).map((u) => u.id);
      const newUserIds = selectedUsers.map((u) => u.value);
      const usersToAdd = newUserIds.filter((id) => !oldUserIds.includes(id));
      const usersToRemove = oldUserIds.filter((id) => !newUserIds.includes(id));

      if (usersToAdd.length > 0) await assignTicketUsers(ticket.id, usersToAdd);
      if (usersToRemove.length > 0) await removeTicketUsers(ticket.id, usersToRemove);

      const oldGroupIds = (ticket.groups ?? []).map((g) => g.id);
      const newGroupIds = selectedGroups.map((g) => g.value);
      const groupesToAdd = newGroupIds.filter((id) => !oldGroupIds.includes(id));
      const groupesToRemove = oldGroupIds.filter((id) => !newGroupIds.includes(id));

      if (groupesToAdd.length > 0) await assignTicketGroupes(ticket.id, groupesToAdd);
      if (groupesToRemove.length > 0) await removeTicketGroupes(ticket.id, groupesToRemove);

      queryClient.invalidateQueries({ queryKey: ["ticket", ticket.id.toString()] });
      queryClient.invalidateQueries({ queryKey: ["tickets"] });

      toast.success("Ticket modifié !");
      setShowEditTicket(false);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal show={showEditTicket} onHide={() => setShowEditTicket(false)} size="lg">
      <Form onSubmit={handleSubmitForm}>
        <Modal.Header closeButton>
          <Modal.Title>
            <h5 style={{ color: "#f6c23e" }}>Modification du ticket</h5>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Row className="mb-3">
            <Col>
              <Form.Label>Titre</Form.Label>
              <Form.Control value={titre} onChange={(e) => setTitre(e.target.value)} />
            </Col>
          </Row>

          <Row className="mb-3">
            <Col>
              <Form.Label>Date début</Form.Label>
              <Form.Control type="date" value={dateDebut} onChange={(e) => setDateDebut(e.target.value)} />
            </Col>
            <Col>
              <Form.Label>Heure</Form.Label>
              <Form.Control type="time" value={heureDebut} onChange={(e) => setHeureDebut(e.target.value)} />
            </Col>
          </Row>

          <Form.Check
            type="checkbox"
            label="Date fin"
            checked={checkdateFin}
            className="mb-2"
            onChange={() => setCheckdateFin(!checkdateFin)}
          />

          {checkdateFin && (
            <Row className="mb-3">
              <Col>
                <Form.Control type="date" value={dateFin} onChange={(e) => setDateFin(e.target.value)} />
              </Col>
              <Col>
                <Form.Control type="time" value={heureFin} onChange={(e) => setHeureFin(e.target.value)} />
              </Col>
            </Row>
          )}

          <Row className="mb-3">
            <Col>
              <Form.Label>Priorité</Form.Label>
              <Select options={optionsPriorite} value={selectedPriorite} onChange={setSelectedPriorite} styles={customSelectStyles} />
            </Col>
            <Col>
              <Form.Label>Type</Form.Label>
              <Select options={optionsType} value={selectedType} onChange={setSelectedType} styles={customSelectStyles} />
            </Col>
          </Row>

          <Row className="mb-3">
            <Col>
              <Form.Label>Description</Form.Label>
              <MarkdownEditor value={description} onChange={setDescription} rows={3} />
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={6}>
              <Form.Label>Utilisateurs</Form.Label>
              <Select
                isMulti
                options={optionsUsers}
                value={selectedUsers}
                onChange={setSelectedUsers}
                styles={customSelectStyles}
                placeholder="Sélectionner des utilisateurs..."
              />
            </Col>
            <Col md={6}>
              <Form.Label>Groupes</Form.Label>
              <Select
                isMulti
                options={optionsGroups}
                value={selectedGroups}
                onChange={setSelectedGroups}
                styles={customSelectStyles}
                placeholder="Sélectionner des groupes..."
              />
            </Col>
          </Row>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" disabled={isSubmitting} onClick={() => setShowEditTicket(false)}>
            Annuler
          </Button>
          <Button variant="warning" type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Chargement..." : "Modifier"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default EditTicket;